/**
 * MarketplaceTab.jsx
 *
 * Marketplace management — business profile (logo from Branding, website from
 * Settings → Profile) + per-service listing toggles. Auto-saves on change.
 */

import React, { useState, useRef, useCallback } from 'react';
import useSWR from 'swr';
import { useSearchParams } from 'react-router-dom';
import TabPageLayout, { PageLoader, EmptyState } from './TabPageLayout';
import { Button } from './ui/button';
import { Label } from './ui/label';
import ServiceToggle from './ServiceToggle';
import { Textarea } from './ui/textarea';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import {
  ExternalLink, Package, Check, Cloud, Image as ImageIcon, Loader2, AlertCircle, ArrowUpRight,
} from 'lucide-react';
import { CATEGORY_OPTIONS } from '../constants/categories';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
  'Content-Type': 'application/json',
});

const fetcher = async (url) => {
  const response = await fetch(url, { headers: getAuthHeaders() });
  if (!response.ok) throw new Error('Failed to fetch');
  return response.json();
};

const MarketplaceTab = ({ organizationId }) => {
  const [, setSearchParams] = useSearchParams();
  const [savingServiceId, setSavingServiceId] = useState(null);
  const [savedFlash, setSavedFlash] = useState(false);
  const [bio, setBio] = useState('');
  const bioRef = useRef('');
  const saveTimer = useRef(null);
  const [initialized, setInitialized] = useState(false);

  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [pendingServiceId, setPendingServiceId] = useState(null);
  const [pendingCategory, setPendingCategory] = useState('');

  const { data: orgData, error: orgError, isLoading: orgLoading, mutate: mutateOrg } = useSWR(
    `${API_URL}/api/v2/auth/organizations/me`,
    fetcher,
    {
      revalidateOnFocus: true,
      onSuccess: (data) => {
        if (!initialized) {
          setBio(data.marketplaceDescription || '');
          bioRef.current = data.marketplaceDescription || '';
          setInitialized(true);
        }
      },
    }
  );

  const { data: servicesData, error: servicesError, isLoading: servicesLoading, mutate: mutateServices } = useSWR(
    organizationId ? `${API_URL}/api/v2/organizations/${organizationId}/services?activeOnly=false` : null,
    fetcher,
    { revalidateOnFocus: true }
  );

  const services = servicesData?.services || [];
  const isLoading = orgLoading || servicesLoading;
  const hasError = orgError || servicesError;

  const logoUrl = orgData?.logoUrl || '';
  const website = orgData?.companyWebsite || '';

  // --- Auto-save the marketplace bio (mirrors Branding/Conversions) ----------
  const persistBio = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/v2/auth/organizations/me`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ marketplaceEnabled: true, marketplaceDescription: bioRef.current }),
      });
      if (!response.ok) throw new Error('save failed');
      mutateOrg();
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 1600);
    } catch (e) {
      toast.error('Failed to save profile');
    }
  }, [mutateOrg]);

  const handleBioChange = (value) => {
    setBio(value);
    bioRef.current = value;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistBio(), 700);
  };

  const toggleServiceListing = async (serviceId, currentValue, hasCategory) => {
    if (!currentValue && !hasCategory) {
      setPendingServiceId(serviceId);
      setPendingCategory('');
      setCategoryModalOpen(true);
      return;
    }
    setSavingServiceId(serviceId);
    try {
      const response = await fetch(`${API_URL}/api/v2/organizations/${organizationId}/services/${serviceId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ marketplaceListed: !currentValue }),
      });
      if (response.ok) {
        mutateServices();
        toast.success(!currentValue ? 'Listed on marketplace' : 'Removed from marketplace');
      } else {
        toast.error('Failed to update service');
      }
    } catch (error) {
      toast.error('Failed to update service');
    } finally {
      setSavingServiceId(null);
    }
  };

  const handleCategoryModalConfirm = async () => {
    if (!pendingCategory) {
      toast.error('Please select a category');
      return;
    }
    setSavingServiceId(pendingServiceId);
    try {
      const response = await fetch(`${API_URL}/api/v2/organizations/${organizationId}/services/${pendingServiceId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ category: pendingCategory, marketplaceListed: true }),
      });
      if (response.ok) {
        mutateServices();
        toast.success('Service listed!');
        setCategoryModalOpen(false);
        setPendingServiceId(null);
        setPendingCategory('');
      } else {
        toast.error('Failed to update service');
      }
    } catch (error) {
      toast.error('Failed to update service');
    } finally {
      setSavingServiceId(null);
    }
  };

  const updateServiceCategory = async (serviceId, newCategory) => {
    setSavingServiceId(serviceId);
    try {
      const response = await fetch(`${API_URL}/api/v2/organizations/${organizationId}/services/${serviceId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ category: newCategory }),
      });
      if (response.ok) {
        mutateServices();
        toast.success('Category updated!');
      } else {
        toast.error('Failed to update category');
      }
    } catch (error) {
      toast.error('Failed to update category');
    } finally {
      setSavingServiceId(null);
    }
  };

  if (isLoading) {
    return (
      <TabPageLayout title="Marketplace" subtitle="List your experiences on the BookingFlows marketplace">
        <PageLoader />
      </TabPageLayout>
    );
  }

  if (hasError) {
    return (
      <TabPageLayout title="Marketplace" subtitle="List your experiences on the BookingFlows marketplace">
        <EmptyState
          icon={<AlertCircle className="w-10 h-10" />}
          title="Unable to load data"
          description="Please refresh the page to try again"
        />
      </TabPageLayout>
    );
  }

  const listedCount = services.filter(s => s.marketplaceListed).length;
  const activeServices = services.filter(s => s.active !== false);

  return (
    <TabPageLayout
      title="Marketplace"
      subtitle="List your experiences on the BookingFlows marketplace"
      actions={
        <div
          data-testid="marketplace-autosave-status"
          className={`hidden sm:inline-flex items-center gap-1.5 text-sm font-medium transition-colors ${savedFlash ? 'text-green-600' : 'text-gray-400'}`}
        >
          {savedFlash ? <Check className="w-4 h-4" /> : <Cloud className="w-4 h-4" />}
          {savedFlash ? 'Saved' : 'Changes save automatically'}
        </div>
      }
    >
      <div className="space-y-5 max-w-2xl">
        {/* Profile Section */}
        <div className="card-soft p-5 sm:p-6" data-testid="marketplace-profile-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="settings-section-title">Business profile</h3>
              <p className="settings-section-subtitle">How your business appears on your public marketplace page</p>
            </div>
            <a
              href={`/host/${organizationId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1 shrink-0"
              data-testid="marketplace-view-profile"
            >
              View page
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="space-y-5">
            {/* Logo — read-only, sourced from the Branding tab */}
            <div>
              <Label className="field-label">Logo</Label>
              <div className="flex items-center gap-4 mt-1">
                <div className="w-14 h-14 rounded-xl bg-gray-50 ring-1 ring-gray-100 flex items-center justify-center overflow-hidden shrink-0">
                  {logoUrl ? (
                    <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" data-testid="marketplace-logo-img" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-gray-300" />
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => setSearchParams({ tab: 'branding' })}
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="marketplace-edit-logo-branding"
                >
                  Edit in Branding
                  <ArrowUpRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <p className="field-hint">Your logo is managed once in the Branding tab and used everywhere.</p>
            </div>

            {/* Bio — auto-saves */}
            <div>
              <Label className="field-label">About</Label>
              <Textarea
                value={bio}
                onChange={(e) => handleBioChange(e.target.value)}
                placeholder="Tell customers what makes your experiences special…"
                rows={2}
                className="mt-1 resize-none"
                maxLength={500}
                data-testid="marketplace-bio"
              />
              <p className="field-hint">{bio.length}/500</p>
            </div>

            {/* Website — read-only, sourced from Settings → Profile */}
            <div>
              <Label className="field-label">Website</Label>
              <Input
                type="url"
                value={website}
                readOnly
                disabled
                placeholder="No website set"
                className="mt-1 bg-gray-50 text-gray-500 cursor-not-allowed"
                data-testid="marketplace-website"
              />
              <p className="field-hint">
                Comes from{' '}
                <button
                  type="button"
                  onClick={() => window.dispatchEvent(new Event('openSettings'))}
                  className="text-blue-600 hover:text-blue-700 font-medium inline-flex items-center gap-0.5"
                  data-testid="marketplace-website-settings-link"
                >
                  Settings → Profile<ArrowUpRight className="w-3 h-3" />
                </button>
              </p>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="card-soft p-5 sm:p-6" data-testid="marketplace-services-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="settings-section-title">Experiences</h3>
              <p className="settings-section-subtitle">Toggle which services appear on the marketplace</p>
            </div>
            <span className="text-sm text-gray-400 shrink-0">{listedCount} listed</span>
          </div>

          {activeServices.length === 0 ? (
            <p className="text-sm text-gray-500">Create services to list them on the marketplace</p>
          ) : (
            <div className="space-y-2.5">
              {activeServices.map((service) => (
                <div
                  key={service.id}
                  className="flex items-center justify-between gap-4 p-3 rounded-xl ring-1 ring-gray-100 hover:ring-gray-200 transition-all"
                  data-testid={`marketplace-service-row-${service.id}`}
                >
                  {/* Image + name only (price/duration live in the Services tab) */}
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-11 h-11 rounded-lg bg-gray-50 overflow-hidden flex-shrink-0 ring-1 ring-gray-100">
                      {service.images?.[0] ? (
                        <img src={service.images[0]} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package className="w-4 h-4 text-gray-300" />
                        </div>
                      )}
                    </div>
                    <p className="font-medium text-gray-900 text-sm truncate">{service.name}</p>
                  </div>

                  <div className="flex items-center gap-3 flex-shrink-0">
                    <Select
                      value={service.category || ''}
                      onValueChange={(value) => updateServiceCategory(service.id, value)}
                      disabled={savingServiceId === service.id}
                    >
                      <SelectTrigger className="w-40 h-9 text-xs" data-testid={`marketplace-category-${service.id}`}>
                        <SelectValue placeholder="Select category">
                          {service.category ? (
                            <span className="flex items-center gap-1.5">
                              <span>{CATEGORY_OPTIONS.find(c => c.id === service.category)?.icon || '🏷️'}</span>
                              <span className="truncate">{CATEGORY_OPTIONS.find(c => c.id === service.category)?.name || service.category}</span>
                            </span>
                          ) : 'Category'}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORY_OPTIONS.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            <span className="flex items-center gap-2">
                              <span>{cat.icon}</span>
                              <span>{cat.name}</span>
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    <ServiceToggle
                      checked={service.marketplaceListed || false}
                      onChange={() => toggleServiceListing(service.id, service.marketplaceListed, !!service.category)}
                      disabled={savingServiceId === service.id}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Live Banner */}
        {listedCount > 0 && (
          <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl ring-1 ring-green-100">
            <div className="flex items-center gap-2 text-green-800">
              <Check className="w-4 h-4" />
              <span className="font-medium text-sm">{listedCount} experience{listedCount > 1 ? 's' : ''} live on marketplace</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open('/', '_blank')}
              className="gap-1.5 h-8 text-green-700 border-green-200 hover:bg-green-100"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              View
            </Button>
          </div>
        )}
      </div>

      {/* Category Modal (first-time listing) */}
      <Dialog open={categoryModalOpen} onOpenChange={setCategoryModalOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Select a category</DialogTitle>
            <DialogDescription>Choose the maritime category to list this experience under</DialogDescription>
          </DialogHeader>

          <Select value={pendingCategory} onValueChange={setPendingCategory}>
            <SelectTrigger className="h-10" data-testid="marketplace-modal-category">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              {CATEGORY_OPTIONS.map((cat) => (
                <SelectItem key={cat.id} value={cat.id}>
                  <span className="flex items-center gap-2">
                    <span>{cat.icon}</span>
                    <span>{cat.name}</span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCategoryModalOpen(false)} className="h-9">Cancel</Button>
            <Button
              onClick={handleCategoryModalConfirm}
              disabled={!pendingCategory || savingServiceId}
              className="bg-blue-600 hover:bg-blue-700 text-white h-9"
              data-testid="marketplace-modal-list-btn"
            >
              {savingServiceId ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              List
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TabPageLayout>
  );
};

export default MarketplaceTab;
